import React, { useEffect, useRef, useState } from "react";
import {
  FaBars,
  FaTimes,
  FaPhoneAlt,
  FaEnvelope,
  FaTelegramPlane,
  FaChevronDown,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalContext } from "../../context/LocalContext";
import "./Header.css";
import TopHeader from "./TopHeader/TopHeader";

const routes = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services", hasDropdown: true },
  { label: "Our Strategies", path: "/our-strategies" },
  { label: "Blogs", path: "/blogs" },
  { label: "Contact Us", path: "/contact" },
];

const servicesQuick = [
  { label: "Microsoft", to: "/services/microsoft" },
  { label: "Accounting & Bookkeeping", to: "/services/accounting-bookkeeping" },
  { label: "Printer Support", to: "/services/printer-support" },
  { label: "Antivirus & EDR", to: "/services/antivirus" },
  { label: "Web Development", to: "/services/web-development" },
  { label: "CRM Development", to: "/services/crm-development" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const dropdownRef = useRef(null);

  const { webinfo = {}, openTelegram, getTelegramUrl } = useLocalContext();
  const telegramHref = getTelegramUrl?.({ preferApp: false }) || "https://t.me/";

  // scroll style
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setShowServices(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // navigation helpers
  const handleNavigate = (path) => {
    setMenuOpen(false);
    setShowServices(false);
    navigate(path);
  };

  // keyboard support for dropdown trigger
  const toggleServices = () => setShowServices((v) => !v);

  return (
    <>
      <TopHeader />

      <header className={`neo-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="neo-bar">
          {/* Logo */}
          <button className="brand" onClick={() => handleNavigate("/")} aria-label="Go to homepage">
            <span className="brand-text">{webinfo.name || "Protrexa"}</span>
          </button>

          {/* Desktop Nav */}
          <nav className="nav-desktop" aria-label="Main">
            {routes.map((r) =>
              r.hasDropdown ? (
                <div
                  key={r.path}
                  className={`nav-item has-dd ${location.pathname.startsWith("/services") ? "active" : ""}`}
                  ref={dropdownRef}
                >
                  <button
                    className="nav-link"
                    aria-haspopup="true"
                    aria-expanded={showServices}
                    onClick={toggleServices}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleServices()}
                  >
                    {r.label}
                    <FaChevronDown className={`chev ${showServices ? "up" : ""}`} />
                  </button>

                  {/* Mega dropdown */}
                  <div className={`mega ${showServices ? "open" : ""}`} role="menu">
                    <div className="mega-grid">
                      {servicesQuick.map((s) => (
                        <button
                          key={s.to}
                          className="mega-item"
                          role="menuitem"
                          onClick={() => handleNavigate(s.to)}
                        >
                          <span className="mi-title">{s.label}</span>
                          <span className="mi-sub">Explore →</span>
                        </button>
                      ))}
                    </div>
                    <div className="mega-foot">
                      <span>Not sure what you need?</span>
                      <button
                        className="btn-line"
                        onClick={() => handleNavigate("/contact")}
                      >
                        Talk to an expert
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={r.path}
                  className={`nav-link ${location.pathname === r.path ? "active" : ""}`}
                  onClick={() => handleNavigate(r.path)}
                >
                  {r.label}
                  <span className="uline" />
                </button>
              )
            )}
          </nav>

          {/* Right actions (desktop) */}
          <div className="act">
            {/* <a
              className="btn-ghost tg"
              href={telegramHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openTelegram?.();
              }}
              aria-label={`Connect on Telegram${webinfo.telegramHandle ? ` @${webinfo.telegramHandle}` : ""}`}
            >
              <FaTelegramPlane />
              <span>Telegram</span>
            </a> */}

            <button
              className="btn-solid call"
              onClick={() => (window.location.href = `tel:${webinfo.phonecall}`)}
              aria-label="Call now"
              title={webinfo.phone}
            >
              <FaPhoneAlt />
              <span>{webinfo.phone || "Call"}</span>
            </button>

            {/* Mobile toggle */}
            <button
              className="hamb"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <FaBars />
            </button>
          </div>
        </div>

        {/* Mobile Drawer */}
        <div className={`drawer ${menuOpen ? "open" : ""}`} role="dialog" aria-modal="true">
          <div className="drawer-head">
            <button className="brand small" onClick={() => handleNavigate("/")}>
              <img src={webinfo.logo} alt={webinfo.name || "Logo"} />
              <span className="brand-text">{webinfo.name || "Protrexa"}</span>
            </button>
            <button className="icon-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">
              <FaTimes />
            </button>
          </div>

          <div className="drawer-links">
            {routes.map((r) =>
              r.hasDropdown ? (
                <details key={r.path} className="dl">
                  <summary>{r.label}</summary>
                  <div className="dl-items">
                    {servicesQuick.map((s) => (
                      <button key={s.to} onClick={() => handleNavigate(s.to)} className="dl-item">
                        {s.label}
                      </button>
                    ))}
                  </div>
                </details>
              ) : (
                <button key={r.path} onClick={() => handleNavigate(r.path)} className="dl">
                  {r.label}
                </button>
              )
            )}
          </div>

          <div className="drawer-actions">
            <a
              className="btn-ghost wide"
              href={telegramHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openTelegram?.();
              }}
            >
              <FaTelegramPlane />
              <span>Connect on Telegram</span>
            </a>

            <button
              className="btn-solid wide"
              onClick={() => (window.location.href = `tel:${webinfo.phonecall}`)}
            >
              <FaPhoneAlt />
              <span>{webinfo.phone || "Call Now"}</span>
            </button>

            <button
              className="btn-line wide"
              onClick={() => (window.location.href = `mailto:${webinfo.email}`)}
            >
              <FaEnvelope />
              <span>{webinfo.email || "Email Us"}</span>
            </button>
          </div>
        </div>
      </header>
    </>
  );
}
